/**
 * Lightweight in-session pub-sub for Space-membership changes.
 *
 * The Spaces sidebar loads membership once via `client.spaces.listMine()`, so a
 * Space created or joined elsewhere in the app wouldn't appear until a reload.
 * Until the provider-backed Spaces store lands (Space-view phase), this notifies
 * interested views (the sidebar) to refetch when membership changes.
 */

import type { PublicSpaceMember } from '@adieuu/shared';

type Listener = () => void;
type MemberUpdatedListener = (spaceId: string, member: PublicSpaceMember) => void;

const listeners = new Set<Listener>();
const memberUpdatedListeners = new Set<MemberUpdatedListener>();

/** Notifies subscribers that the current Alias's Space membership changed. */
export function emitSpacesChanged(): void {
  for (const listener of [...listeners]) {
    try {
      listener();
    } catch {
      // A failing subscriber must not prevent the others from being notified.
    }
  }
}

/** Subscribes to membership changes; returns an unsubscribe function. */
export function onSpacesChanged(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Notifies subscribers that a Space member's profile (nickname/colour) changed. */
export function emitSpaceMemberUpdated(spaceId: string, member: PublicSpaceMember): void {
  for (const listener of [...memberUpdatedListeners]) {
    try {
      listener(spaceId, member);
    } catch {
      // A failing subscriber must not prevent the others from being notified.
    }
  }
}

/** Subscribes to Space member profile updates; returns an unsubscribe function. */
export function onSpaceMemberUpdated(listener: MemberUpdatedListener): () => void {
  memberUpdatedListeners.add(listener);
  return () => {
    memberUpdatedListeners.delete(listener);
  };
}

type MemberJoinedListener = (spaceId: string, member: PublicSpaceMember) => void;
type MemberLeftListener = (spaceId: string, identityId: string) => void;

const memberJoinedListeners = new Set<MemberJoinedListener>();
const memberLeftListeners = new Set<MemberLeftListener>();

/** Someone else joined the Space (working-set upsert). */
export function emitSpaceMemberJoined(spaceId: string, member: PublicSpaceMember): void {
  for (const listener of [...memberJoinedListeners]) {
    try {
      listener(spaceId, member);
    } catch {
      // A failing subscriber must not prevent the others from being notified.
    }
  }
}

export function onSpaceMemberJoined(listener: MemberJoinedListener): () => void {
  memberJoinedListeners.add(listener);
  return () => {
    memberJoinedListeners.delete(listener);
  };
}

/** Someone else left, was kicked, or was banned (working-set removal). */
export function emitSpaceMemberLeft(spaceId: string, identityId: string): void {
  for (const listener of [...memberLeftListeners]) {
    try {
      listener(spaceId, identityId);
    } catch {
      // A failing subscriber must not prevent the others from being notified.
    }
  }
}

export function onSpaceMemberLeft(listener: MemberLeftListener): () => void {
  memberLeftListeners.add(listener);
  return () => {
    memberLeftListeners.delete(listener);
  };
}

type MessageMembershipListener = (
  spaceId: string,
  authorIds: readonly string[],
  members: readonly PublicSpaceMember[],
) => void;

const messageMembershipListeners = new Set<MessageMembershipListener>();

function uniqueMessageAuthorIds(
  messages: readonly ({ fromIdentityId?: string; replyToMessageAuthorId?: string } | null | undefined)[],
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const m of messages) {
    if (!m) continue;
    for (const id of [m.fromIdentityId, m.replyToMessageAuthorId]) {
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

/**
 * Membership sidecar from a Space message/pin/around/getMessage HTTP read.
 * `members` omitted (legacy) is ignored so we never treat the whole page as departed.
 */
export function emitSpaceMessageReadMembership(
  spaceId: string,
  messages: readonly ({ fromIdentityId?: string; replyToMessageAuthorId?: string } | null | undefined)[],
  members: readonly PublicSpaceMember[] | undefined,
): void {
  if (!members) return;
  emitSpaceMessageMembership(spaceId, uniqueMessageAuthorIds(messages), members);
}

/** Working-set ingest for authors on one HTTP message page. */
export function emitSpaceMessageMembership(
  spaceId: string,
  authorIds: readonly string[],
  members: readonly PublicSpaceMember[],
): void {
  for (const listener of [...messageMembershipListeners]) {
    try {
      listener(spaceId, authorIds, members);
    } catch {
      // A failing subscriber must not prevent the others from being notified.
    }
  }
}

export function onSpaceMessageMembership(listener: MessageMembershipListener): () => void {
  messageMembershipListeners.add(listener);
  return () => {
    messageMembershipListeners.delete(listener);
  };
}

const inviteListeners = new Set<Listener>();

/**
 * Notifies subscribers that the viewer's Space invite inbox changed.
 *
 * Carries no payload on purpose. Invite events arrive on the identity channel
 * and each one names a Space the recipient may not have accepted yet, so
 * subscribers refetch through the authorized endpoint rather than trusting a
 * pushed row.
 */
export function emitSpaceInvitesChanged(): void {
  for (const listener of [...inviteListeners]) {
    try {
      listener();
    } catch {
      // A failing subscriber must not prevent the others from being notified.
    }
  }
}

/** Subscribes to Space invite inbox changes; returns an unsubscribe function. */
export function onSpaceInvitesChanged(listener: Listener): () => void {
  inviteListeners.add(listener);
  return () => {
    inviteListeners.delete(listener);
  };
}
